'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import {
  cn,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  OrgFunctionKindBadge
} from '@bliks-io/ui';
import { useFilteredItems } from './useFilteredItems';
import { orgFunctionKindIcon } from '@/constants/entityIcons';
import type { EntitySelectFilter } from './select.types';

interface OrgFunctionSelectProps {
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Allow clearing the selection (useful for optional fields) */
  allowClear?: boolean;
  /** Custom label for the clear/all option */
  clearLabel?: string;
  /** Optional callback for custom clear behavior. Defaults to onChange(undefined). */
  onClear?: () => void;
  /** Client-side filter applied before the internal search filter. */
  filter?: EntitySelectFilter<NonNullable<ReturnType<typeof useOrgFunctions>['data']>[number]>;
}

/**
 * Standalone OrgFunction select (single-select) over BOTH kinds (team + role).
 * Used by the suggestion-review merge panel to pick an existing OrgFunction to
 * merge a OrgFunction suggestion into, over the unified OrgFunction list.
 */
export const OrgFunctionSelect = React.forwardRef<HTMLButtonElement, OrgFunctionSelectProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      className,
      id,
      name,
      allowClear = false,
      clearLabel,
      onClear,
      filter
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { data: orgFunctions, isLoading, isError } = useOrgFunctions();

    const [searchQuery, setSearchQuery] = React.useState('');

    const fieldId = id || name;
    const isDisabled =
      disabled || isLoading || isError || !orgFunctions || orgFunctions.length === 0;

    let placeholderText = placeholder || t('orgFunctionSelect.placeholder');
    if (isLoading) {
      placeholderText = t('orgFunctionSelect.loading');
    } else if (isError || !orgFunctions) {
      placeholderText = t('orgFunctionSelect.error');
    } else if (orgFunctions.length === 0) {
      placeholderText = t('orgFunctionSelect.noOrgFunctions');
    }

    const searchFilter = React.useCallback(
      (orgFunction: NonNullable<typeof orgFunctions>[number]) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const fname = orgFunction.name?.toLowerCase() || '';
        const description = orgFunction.description?.toLowerCase() || '';
        const email = orgFunction.email?.toLowerCase() || '';
        return fname.includes(q) || description.includes(q) || email.includes(q);
      },
      [searchQuery]
    );

    const filteredOrgFunctions = useFilteredItems(orgFunctions, filter, searchFilter);

    const selectedOrgFunction = React.useMemo(() => {
      if (!orgFunctions || !value) return undefined;
      return orgFunctions.find((orgFunction) => orgFunction._id === value);
    }, [orgFunctions, value]);

    const triggerContent = React.useMemo(() => {
      if (!selectedOrgFunction) return null;
      return (
        <span className="flex items-center gap-2 min-w-0">
          {orgFunctionKindIcon(selectedOrgFunction.kind)}
          <span className="truncate">{selectedOrgFunction.name}</span>
        </span>
      );
    }, [selectedOrgFunction]);

    const handleClearSelect = React.useCallback(() => {
      if (onClear) {
        onClear();
      } else {
        onChange(undefined);
      }
    }, [onClear, onChange]);

    return (
      <Combobox
        value={value}
        onValueChange={onChange}
        disabled={isDisabled}
        multiple={false}
        onClear={onClear}
      >
        <ComboboxTrigger
          ref={ref}
          id={fieldId}
          placeholder={placeholderText}
          allowClear={allowClear}
          className={className}
        >
          {triggerContent}
        </ComboboxTrigger>
        <ComboboxContent>
          <ComboboxInput
            placeholder={t('orgFunctionSelect.search')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <ComboboxList>
            <ComboboxEmpty>{t('orgFunctionSelect.noResults')}</ComboboxEmpty>
            {allowClear && (
              <ComboboxItem value="__clear__" onSelect={handleClearSelect} className="gap-2">
                <span className={cn('flex-1', !selectedOrgFunction && 'font-semibold')}>
                  {clearLabel || t('orgFunctionSelect.noOrgFunction')}
                </span>
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    !selectedOrgFunction ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </ComboboxItem>
            )}
            {filteredOrgFunctions.map((orgFunction) => (
              <ComboboxItem key={orgFunction._id} value={orgFunction._id}>
                <div className="flex-1 flex items-center gap-2">
                  {orgFunctionKindIcon(orgFunction.kind)}
                  <span className="font-medium">{orgFunction.name}</span>
                  {/* The list mixes both kinds — the badge disambiguates a
                      team and a role sharing the same name. */}
                  <OrgFunctionKindBadge
                    kind={orgFunction.kind}
                    label={t(`organization.orgFunctions.kind.${orgFunction.kind}` as const)}
                  />
                  {orgFunction.email && (
                    <span className="text-muted-foreground">{orgFunction.email}</span>
                  )}
                </div>
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }
);

OrgFunctionSelect.displayName = 'OrgFunctionSelect';
